import { useEffect } from "react";

/**
 * Custom React hook that triggers a handler function when a click occurs outside the specified element.
 *
 * @param {React.RefObject<HTMLElement>} ref - The ref of the element to detect outside clicks for.
 * @param {(event: MouseEvent) => void} handler - The function to call when a click outside the element is detected.
 *
 * @example
 * const ref = useRef();
 * useClickOutside(ref, () => setOpen(false));
 */
const useClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }

      // Add this line to confirm the hook is triggering:
      // console.log("Clicked outside!");

      handler(event); // this will trigger onClose
    };

    document.addEventListener("mousedown", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
    };
  }, [ref, handler]);
};

export default useClickOutside;
  